import type { PublicChatMessage } from "../api/publicChat";

// Key derivation helpers. When variant === "default" the keys are identical to
// the legacy un-namespaced names so that existing callers and stored data are
// unaffected across deploys. Any other variant gets a ":<variant>" suffix,
// keeping keys from different embeds on the same page isolated.
const baseKey = (variant: string): string =>
  variant === "default"
    ? "public-jan-session"
    : `public-jan-session:${variant}`;
const baseChatKey = (variant: string): string =>
  variant === "default"
    ? "public-jan-chat-id"
    : `public-jan-chat-id:${variant}`;
const baseMsgKey = (variant: string): string =>
  variant === "default"
    ? "public-jan-tab-messages"
    : `public-jan-tab-messages:${variant}`;

export const getToken = (variant = "default"): string | null =>
  localStorage.getItem(baseKey(variant));
export const setToken = (t: string, variant = "default"): void =>
  localStorage.setItem(baseKey(variant), t);
export const clearToken = (variant = "default"): void => {
  localStorage.removeItem(baseKey(variant));
  localStorage.removeItem(baseChatKey(variant));
  sessionStorage.removeItem(baseMsgKey(variant));
};

export const getChatId = (variant = "default"): string | null =>
  localStorage.getItem(baseChatKey(variant));
export const setChatId = (id: string, variant = "default"): void =>
  localStorage.setItem(baseChatKey(variant), id);

// Per-tab visible message history. We persist messages to sessionStorage so a
// same-tab refresh restores the conversation, but a new tab (fresh
// sessionStorage) starts empty — even though the server still has every
// message under the shared chat_id. This avoids cross-tab leakage of prior
// conversations on the unauthenticated surface. See NOV-1104 / "Jan Public:
// Chat persistence".
export const getSavedMessages = (
  variant = "default"
): PublicChatMessage[] | null => {
  const raw = sessionStorage.getItem(baseMsgKey(variant));
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as PublicChatMessage[];
  } catch {
    // Corrupt entry — drop it so we don't re-parse the same bad value on
    // every bootstrap.
    sessionStorage.removeItem(baseMsgKey(variant));
    return null;
  }
};

export const saveMessages = (
  messages: PublicChatMessage[],
  variant = "default"
): void => {
  try {
    sessionStorage.setItem(baseMsgKey(variant), JSON.stringify(messages));
  } catch {
    // Quota exceeded or storage disabled — ignore silently. The user will
    // still see live messages from React state; only refresh restoration
    // breaks, which is acceptable degradation.
  }
};
